import { Inject, Injectable, InjectionToken } from '@angular/core'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Observable, from, throwError } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import { UserAuthService } from './user-auth.service'
import { Router } from '@angular/router'
import { UserActiveService } from './user-active.service'
import { GtmTrackingService } from './gtm-tracking.service'
import { LoaderService } from './loader.service'
import { SplunkService } from '../common/services/splunk.service'
import {v4 as uuidv4} from 'uuid';

export const UUID_TOKEN = new InjectionToken<string>('UUID_TOKEN');

export const generateUuidV4 = (): string => {
  return uuidv4();
};
interface HttpErrorResponses extends HttpErrorResponse {
  errors: any[];
}
@Injectable()
export class GlobalHttpInterceptor implements HttpInterceptor {
    private sessionKey: string;
    constructor (private authService: UserAuthService, private inactiveService: UserActiveService, private router: Router,
        private loaderService: LoaderService, private gtmService: GtmTrackingService,private splunkService: SplunkService,
        @Inject(UUID_TOKEN) private uuid: string) {
           this.sessionKey = this.uuid;
        }
    intercept (req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip auth token injection and error handling for agent endpoints
        if (req.headers.has('X-Skip-Interceptor')) {
            const cleanReq = req.clone({ headers: req.headers.delete('X-Skip-Interceptor') })
            return next.handle(cleanReq)
        }
        return from(this.getCurrentAuthSession(req)).pipe(
            switchMap((token) => {
                const clonedRequest = req.clone({
                  setHeaders: {
                    Authorization: token ? `Bearer ${token}` : ''
                  }
                })
                return next.handle(clonedRequest)
            }), catchError((error) => this.handleError(error))
        )
    }
    async getCurrentAuthSession (req?: any): Promise<string> {
        try {
            const session = await fetchAuthSession()
            const accessToken = session?.tokens?.accessToken?.toString() || ''
            return accessToken
        } catch (error) {
            console.error('Error fetching session:', error)
            if (error === 'No current user') {
                this.signOut()
            }
            return ''
        }
    }
    async signOut () {
        try {
            await signOut({ global: true })
            this.gtmService.setOrGetClientTracking()
            this.gtmService.setClientTrackingWithLoggedInUser()
            this.inactiveService.stopMonitoring()
            this.authService.setUserId('')
            this.authService.setIdpUser(false)
            this.authService.deleteCookie('referrerUrl')
            this.authService.deleteCookie('previousPageUrl')
            this.authService.deleteCognitoCookies()
            this.router.navigate(['/login-initiate']).then(() => {
                window.location.reload()
            })
        } catch (error) {
            console.error('Error during sign-out:', error)
        }
    }

    handleError (error: any): Observable<never> {
      this.loaderService.hide()
      if (error instanceof HttpErrorResponse) {
          if (error.status === 401 || error.status === 403) {
            this.signOut()
          } else {
            this.router.navigate(['/server-error'])
          }
      }
      this.splunkLogger(error, this.sessionKey);
      return throwError(() => error)
    }

     private splunkLogger(error: HttpErrorResponses, sessionKey: string): void {
    const payload = this.splunkService.generatePayload({
      session_key: sessionKey,
      source_site: 'One_Account_Api',
      event_type: 'API Error',
      error_error: error?.error,
      error_name: error?.status,
      error_message: error?.message,
      error_stack: new Error().stack,
    });
    this.splunkService.logger(payload, error);
  }
}
